/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import UtComparisonPage from './UtComparisonPage';
import { useUtComparisonStore } from './store/useUtComparisonStore';
import { AnalysisRow, UtCellData, UtKey, UtMatrixOption } from './types';
import { UT_TYPES } from './constants';
import { fillStatusNames } from './api';
import { getReportDetail } from 'apis/ut/UtMatrixComparisonReportList';

const emptyCell = (): UtCellData => ({
  usage: [0, 0],
  unit: [0, 0],
  qty: [0, 0],
  diffU: 0,
  diffQ: 0,
});

/**
 * 저장된 RPT_D 행 → AnalysisRow[]
 *
 * 저장 시(UtMatrixSummaryPage의 machineRowsToRptDetail) AnalysisRow 1건을 UT 항목별로
 * 펼쳤으므로, 복원할 때는 설비 키(공정/설비군/세부공정/라인/Main/Machine)로 다시 묶고
 * UTM_TP_CD로 UT 항목 칸을 채운다. 1번 슬롯=전, 2번 슬롯=후.
 */
const rptDetailToAnalysisRows = (details: any[]): AnalysisRow[] => {
  const utKeys = new Set<string>(UT_TYPES.map((u) => u.key));
  const map = new Map<string, AnalysisRow>();

  for (const d of details || []) {
    const process = d.prdnProcNm ?? '';
    const eqGroup = d.eqclNm ?? '';
    const subProcess = d.dtalProcNm ?? '';
    const line = d.prdnLnNm ?? '';
    const main = d.eqpMainNm ?? '';
    const machine = d.eqpMchNm ?? '';
    const key = [process, eqGroup, subProcess, line, main, machine].join('||');

    let row = map.get(key);
    if (!row) {
      const ut = {} as Record<UtKey, UtCellData>;
      for (const u of UT_TYPES) ut[u.key] = emptyCell();
      row = {
        machine,
        main,
        process,
        eqGroup,
        subProcess,
        line,
        hasDiff: false,
        machineHasDiff: false,
        machineKey: machine,
        lineQty: [0, 0],
        ut,
      };
      map.set(key, row);
    }

    // UTM_TP_CD가 UT 항목키가 아닌 행(구버전 등)은 채울 칸이 없으므로 건너뛴다
    const tpCd = String(d.utmTpCd ?? '');
    if (!utKeys.has(tpCd)) continue;

    const usage = [Number(d.tot1Usg ?? 0), Number(d.tot2Usg ?? 0)];
    const unit = [Number(d.unit1Usg ?? 0), Number(d.unit2Usg ?? 0)];
    const qty = [Number(d.atc1Qty ?? 0), Number(d.atc2Qty ?? 0)];
    const cell: UtCellData = {
      usage,
      unit,
      qty,
      diffU: usage[1] - usage[0],
      diffQ: qty[1] - qty[0],
    };
    row.ut[tpCd as UtKey] = cell;
    if (cell.diffU !== 0) row.hasDiff = true;
  }

  // machineHasDiff는 저장 당시와 동일하게 Machine 단위 합계 기준으로 다시 판정
  const rows = Array.from(map.values());
  const machineDiff = new Map<string, number>();
  rows.forEach((row) => {
    const sum = UT_TYPES.reduce((acc, u) => acc + (row.ut[u.key]?.diffU || 0), 0);
    machineDiff.set(row.machineKey, (machineDiff.get(row.machineKey) || 0) + sum);
  });
  rows.forEach((row) => {
    row.machineHasDiff = Math.abs(machineDiff.get(row.machineKey) || 0) > 1e-9;
  });

  return rows;
};

interface Props {
  rptId?: string;
}

const UtComparisonReportPage = ({ rptId }: Props) => {
  const location = useLocation();
  const matrices = ((location.state as any)?.matrices || []) as UtMatrixOption[];
  const hasMatrices = matrices.length > 0;
  const setSelectedMatrices = useUtComparisonStore((s) => s.setSelectedMatrices);
  const loadHistorySnapshot = useUtComparisonStore((s) => s.loadHistorySnapshot);
  const runAnalyze = useUtComparisonStore((s) => s.runAnalyze);
  const reset = useUtComparisonStore((s) => s.reset);
  // 마지막으로 로드한 rptId (props로 rptId가 바뀌면 다시 로드)
  const seededRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (rptId) {
      if (seededRef.current === rptId) return;
      seededRef.current = rptId as string;
      setLoading(true);
      setError(null);
      // 저장된 리포트 조회 — 재분석하지 않고 저장 당시 스냅샷을 그대로 그린다
      getReportDetail(rptId)
        .then(async (detail) => {
          // 상태명 키는 응답에 따라 utmStatNm(RptUtmTarget)일 수도 있어 둘 다 받는다.
          // RPT_UTM_M에 상태가 저장되지 않아 아예 안 내려올 수도 있어 fillStatusNames로 메운다.
          // 구분(기준/비교대상/참조)을 배열 순서로 판정하므로 SORT_ORD 순서에 의존한다.
          // 매퍼(selectRptUtmTargets)도 ORDER BY r.SORT_ORD로 내려주지만, 화면 라벨이
          // 순서에 직결되는 값이라 여기서도 한 번 보장한다(getRptTargets 경로와 동일).
          const targets: UtMatrixOption[] = [...(detail?.targets || [])]
            .sort((a: any, b: any) => Number(a?.sortOrd ?? 0) - Number(b?.sortOrd ?? 0))
            .map((t: any) => ({
              utmId: String(t.utmId || ''),
              utmNo: String(t.utmNo || ''),
              verNo: String(t.verNo || ''),
              utmNm: String(t.utmNm || ''),
              statusNm: String(t.utmStatNm || t.statusNm || ''),
            }));
          // 통합 API는 machDetails로 내려준다 (details는 구버전 응답 호환)
          loadHistorySnapshot({
            rows: rptDetailToAnalysisRows(detail?.machDetails ?? detail?.details),
            targets: await fillStatusNames(targets),
            historyRptId: rptId,
          });
        })
        .catch((e) => {
          console.error(e);
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => setLoading(false));
    } else if (hasMatrices) {
      if (seededRef.current === 'matrices') return;
      seededRef.current = 'matrices';
      setSelectedMatrices(matrices);
      runAnalyze();
    }
  }, [hasMatrices, rptId]);

  useEffect(() => {
    return () => reset();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#2d9bb2', fontSize: 12 }}>
        이력을 불러오는 중...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          margin: 16,
          border: '1px solid #fda293',
          background: '#fef1eb',
          color: '#b32542',
          borderRadius: 3,
          padding: '10px 14px',
          fontSize: 12,
        }}
      >
        이력 조회 오류: {error}
      </div>
    );
  }
  return <UtComparisonPage />;
};
export default UtComparisonReportPage;
